import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArchiveRestore, ArrowLeft, Copy, Package, Pencil, QrCode } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { uuid4 } from "../../hooks/useFlowOperationId";
import { AdminPageHeader } from "../../components/admin/DataTable";
import { PhotoGallery } from "../../components/photo/PhotoGallery";
import { QrPrintDialog } from "../../components/admin/QrPrintDialog";
import { AddArticleStockDialog } from "../../components/admin/AddArticleStockDialog";
import { ArtikelFormDialog } from "../../components/admin/ArtikelFormDialog";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminArtikelDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [addStockOpen, setAddStockOpen] = useState(false);
    const [stockMessage, setStockMessage] = useState("");
    const [stock, setStock] = useState([]);
    const [movements, setMovements] = useState([]);
    const [categories, setCategories] = useState([]);
    const [assets, setAssets] = useState([]);
    const [compartments, setCompartments] = useState([]);
    const [qrOpen, setQrOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [cloneBusy, setCloneBusy] = useState(false);
    const [cloneError, setCloneError] = useState("");
    const [clonedId, setClonedId] = useState(null);
    const cloneInFlight = useRef(false);
    const currentArticleId = useRef(id);
    currentArticleId.current = id;
    const [galleryKey, setGalleryKey] = useState(0);
    // Mengenkorrektur: posId -> Eingabewert (String); leer = unverändert.
    const [drafts, setDrafts] = useState({});
    const [korrOpen, setKorrOpen] = useState(false);
    const [korrComment, setKorrComment] = useState("");
    const [korrBusy, setKorrBusy] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [lifecycleBusy, setLifecycleBusy] = useState(false);

    const load = useCallback(() => {
        Promise.all([
            rpc.get(`/master/articles`),
            rpc.get(`/master/stock-positions`),
            rpc.get(`/movements?article_id=${id}&limit=30`),
            rpc.get(`/master/categories`),
            rpc.get(`/master/assets?article_id=${id}`),
            rpc.get(`/master/compartments`),
        ]).then(([a, s, m, c, as, k]) => {
            if (currentArticleId.current !== id) return;
            if (a.ok) setArticle((current) => {
                const next = a.data.find((x) => x.id === id) || null;
                // Preserve the edit form while the newly cloned article loads.
                return current && next && current.id === next.id && current.updated_at === next.updated_at ? current : next;
            });
            if (s.ok) setStock(s.data.filter((p) => p.article_id === id && p.quantity > 0));
            if (m.ok) setMovements(m.data || []);
            if (c.ok) setCategories(c.data);
            if (as.ok) setAssets(as.data);
            if (k.ok) setCompartments(k.data);
        });
    }, [id]);
    useEffect(load, [load]);

    const cloneArticle = async () => {
        if (!article || cloneInFlight.current) return;
        cloneInFlight.current = true;
        setCloneBusy(true);
        setCloneError("");
        try {
            const res = await rpc.post(`/master/articles/${id}/clone`);
            if (!res.ok) {
                setCloneError(res.error || "Klonen fehlgeschlagen. Bitte erneut versuchen.");
                return;
            }
            currentArticleId.current = res.data.id;
            setArticle(res.data);
            setStock([]);
            setMovements([]);
            setAssets([]);
            setDrafts({});
            setClonedId(res.data.id);
            navigate(`/admin/artikel/${res.data.id}`);
            setEditOpen(true);
        } finally {
            cloneInFlight.current = false;
            setCloneBusy(false);
        }
    };

    const isArchived = article?.lifecycle === "archived";
    const lifecycleLabel = isArchived ? "archiviert" : "aktiv";

    // Archivieren/Reaktivieren: reiner Stammdaten-PATCH; Bestände, Verlauf
    // und Audit bleiben unberührt. Archivierte Artikel fehlen in Floor-Liste
    // und Nachbestellung (Backend-Regel).
    const setLifecycle = async (lifecycle) => {
        setLifecycleBusy(true);
        const res = await rpc.patch(`/master/articles/${id}`, { lifecycle });
        setLifecycleBusy(false);
        if (!res.ok) return toast.error(res.error || "Änderung fehlgeschlagen.");
        toast.success(lifecycle === "archived" ? "Artikel archiviert." : "Artikel reaktiviert.");
        setArchiveOpen(false);
        load();
    };

    const category = categories.find((c) => c.id === article?.category_id);
    const totalStock = stock.reduce((s, p) => s + (p.quantity || 0), 0);
    const compMap = Object.fromEntries(compartments.map((c) => [c.id, c]));
    const compCode = (p) => compMap[p.compartment_id]?.code || p.compartment_id?.slice(0, 8);

    const draftValue = (p) => (drafts[p.id] !== undefined ? drafts[p.id] : String(p.quantity));
    const changed = stock.filter((p) => {
        const d = drafts[p.id];
        if (d === undefined || d === "") return false;
        const n = Number(d);
        return Number.isInteger(n) && n >= 0 && n !== p.quantity;
    });

    // Bucht je geänderter Position eine Korrektur über den Buchungskern
    // (adjustment, absoluter Zielstand) -- ein gemeinsamer Kommentar für alle.
    const submitKorrektur = async () => {
        if (!korrComment.trim()) return toast.error("Kommentar ist Pflicht bei Korrektur.");
        setKorrBusy(true);
        let okCount = 0;
        for (const p of changed) {
            const res = await rpc.post("/movements", {
                client_operation_id: uuid4(),
                type: "adjustment",
                from_position_id: p.id,
                quantity: Number(drafts[p.id]),
                reason: "count_difference",
                comment: korrComment.trim(),
            });
            if (res.ok) okCount += 1;
            else toast.error(`${compCode(p)}: ${res.error}`);
        }
        setKorrBusy(false);
        if (okCount > 0) {
            toast.success(okCount === 1 ? "Korrektur gebucht." : `${okCount} Korrekturen gebucht.`);
        }
        setKorrOpen(false);
        setKorrComment("");
        setDrafts({});
        load();
    };

    return (
        <>
            <AdminPageHeader wrapActions title={article?.name || "Artikel"} subtitle={article?.ean || article?.id}>
                <Button
                    variant="ghost"
                    // #136 Befund 6: zurück in die Liste MIT Filter/Suche/Seite
                    // (History-Eintrag trägt die Query); Fallback bei Direkteinstieg.
                    onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/admin/artikel"))}
                    data-testid="artikel-detail-back"
                >
                    <ArrowLeft className="mr-1 h-4 w-4" /> Zurück
                </Button>
                <Button variant="outline" onClick={() => setEditOpen(true)} disabled={!article} data-testid="artikel-detail-edit">
                    <Pencil className="mr-1 h-4 w-4" /> Bearbeiten
                </Button>
                <Button variant="outline" className="pw-tap hover:bg-muted hover:text-foreground focus-visible:ring-foreground" onClick={cloneArticle} disabled={!article || cloneBusy} aria-busy={cloneBusy} data-testid="artikel-detail-clone">
                    <Copy className="mr-1 h-4 w-4" aria-hidden="true" /> {cloneBusy ? "Wird geklont …" : "Artikel klonen"}
                </Button>
                <Button variant="outline" onClick={() => setQrOpen(true)} data-testid="artikel-detail-qr">
                    <QrCode className="mr-1 h-4 w-4" /> QR drucken
                </Button>
                {article && !isArchived ? (
                    <Button variant="outline" onClick={() => setArchiveOpen(true)} data-testid="artikel-detail-archive">
                        <Archive className="mr-1 h-4 w-4" /> Archivieren
                    </Button>
                ) : null}
                {article && isArchived ? (
                    <Button
                        variant="outline"
                        onClick={() => setLifecycle("active")}
                        disabled={lifecycleBusy}
                        data-testid="artikel-detail-reactivate"
                    >
                        <ArchiveRestore className="mr-1 h-4 w-4" /> Reaktivieren
                    </Button>
                ) : null}
            </AdminPageHeader>
            {cloneError ? <p role="alert" className="mb-4 text-sm text-destructive">{cloneError}</p> : null}
            {clonedId === id ? (
                <p role="status" className="mb-4 text-sm text-foreground">
                    Kopie erstellt. Hersteller-Nr. und EAN sind leer; Bestand und Buchungen wurden nicht übernommen.
                </p>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-3">
                <div className="rounded-lg border border-border bg-card/40 p-4 lg:col-span-2">
                    <div className="mb-2 flex items-center gap-2 text-sm">
                        <Package className="h-4 w-4 text-primary" />
                        <span className="font-semibold">Stammdaten</span>
                    </div>
                    {article ? (
                        <dl className="grid grid-cols-2 gap-y-2 text-xs">
                            <dt className="text-muted-foreground">Typ</dt>
                            <dd>{article.type}</dd>
                            <dt className="text-muted-foreground">Kategorie</dt>
                            <dd>{category?.name || "—"}</dd>
                            <dt className="text-muted-foreground">EAN</dt>
                            <dd>{article.ean || "—"}</dd>
                            <dt className="text-muted-foreground">Hersteller-Nr.</dt>
                            <dd>{article.manufacturer_number || "—"}</dd>
                            <dt className="text-muted-foreground">Mindestbestand</dt>
                            <dd>{article.min_quantity ?? "—"}</dd>
                            <dt className="text-muted-foreground">Lifecycle</dt>
                            <dd data-testid="artikel-detail-lifecycle">{lifecycleLabel}</dd>
                            <dt className="text-muted-foreground">Gesamt-Bestand</dt>
                            <dd className="font-semibold">{totalStock}</dd>
                            {article.type === "single" ? (
                                <>
                                    <dt className="text-muted-foreground">Ausleihgegenstände</dt>
                                    <dd>{assets.length}</dd>
                                </>
                            ) : null}
                        </dl>
                    ) : (
                        <div className="text-xs text-muted-foreground">Artikel nicht gefunden.</div>
                    )}
                </div>
                <div className="rounded-lg border border-border bg-card/40 p-4">
                    <div className="mb-2 text-sm font-semibold">Bestands-Positionen</div>
                    <Button variant="outline" className="mb-3 min-h-11 focus-visible:ring-foreground" disabled={!article || isArchived} onClick={() => { setStockMessage(""); setAddStockOpen(true); }}>Lagerort hinzufügen</Button>
                    {stockMessage ? <p role="status" className="mb-3 text-sm">{stockMessage}</p> : null}
                    {stock.length === 0 ? (
                        <div className="text-xs text-muted-foreground">Keine Bestands-Positionen.</div>
                    ) : (
                        <>
                            <ul className="flex flex-col gap-1 text-xs">
                                {stock.map((p) => (
                                    <li key={p.id} className="flex items-center justify-between gap-2 border-b border-border/40 py-1">
                                        <span className="truncate">
                                            {compCode(p)}
                                            {(p.blocked_quantity || 0) > 0 ? (
                                                <span className="ml-1 text-muted-foreground">
                                                    ({p.blocked_quantity} reserviert)
                                                </span>
                                            ) : null}
                                        </span>
                                        <Input
                                            type="number"
                                            min={0}
                                            step={1}
                                            aria-label={`Bestand ${compCode(p)}`}
                                            value={draftValue(p)}
                                            onChange={(e) => setDrafts({ ...drafts, [p.id]: e.target.value })}
                                            className="h-8 w-24 text-right font-mono"
                                            data-testid={`artikel-detail-pos-qty-${p.id}`}
                                        />
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-3 flex items-center justify-between gap-2">
                                <span className="text-xs text-muted-foreground">
                                    Änderungen werden als Korrektur gebucht.
                                </span>
                                <Button
                                    size="sm"
                                    disabled={changed.length === 0}
                                    onClick={() => setKorrOpen(true)}
                                    data-testid="artikel-detail-pos-save"
                                >
                                    Mengen korrigieren
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="mt-4 rounded-lg border border-border bg-card/40 p-4">
                <div className="mb-3 text-sm font-semibold">Fotos</div>
                <PhotoGallery
                    entityType="article"
                    entityId={id}
                    isAdmin={true}
                    showFilters={false}
                    testId="artikel-detail-gallery"
                    refreshKey={galleryKey}
                />
            </div>

            <Dialog open={korrOpen} onOpenChange={(v) => !v && setKorrOpen(false)}>
                <DialogContent data-testid="artikel-detail-korrektur-dialog">
                    <DialogHeader>
                        <DialogTitle>Mengen korrigieren</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-3">
                        <ul className="flex flex-col gap-1 text-xs">
                            {changed.map((p) => (
                                <li key={p.id} className="flex justify-between border-b border-border/40 py-1">
                                    <span>{compCode(p)}</span>
                                    <span className="font-mono">
                                        {p.quantity} → {Number(drafts[p.id])}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-xs text-muted-foreground">
                            Wird als Korrektur (Zähl-Differenz) im Verlauf gebucht.
                        </p>
                        <Label>Kommentar (Pflicht)</Label>
                        <Input
                            value={korrComment}
                            onChange={(e) => setKorrComment(e.target.value)}
                            data-testid="artikel-detail-korrektur-comment"
                        />
                        <Button
                            onClick={submitKorrektur}
                            disabled={korrBusy || !korrComment.trim()}
                            data-testid="artikel-detail-korrektur-submit"
                        >
                            {korrBusy ? "..." : changed.length === 1 ? "1 Korrektur buchen" : `${changed.length} Korrekturen buchen`}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={archiveOpen} onOpenChange={(v) => !v && setArchiveOpen(false)}>
                <DialogContent data-testid="artikel-detail-archive-dialog">
                    <DialogHeader>
                        <DialogTitle>Artikel archivieren</DialogTitle>
                        <DialogDescription>
                            „{article?.name}" wird aus der Artikelliste im Arbeitsmodus und aus der
                            Nachbestellung ausgeblendet. Bestände, Verlauf und Audit bleiben erhalten;
                            der Artikel lässt sich jederzeit reaktivieren.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setArchiveOpen(false)} data-testid="artikel-detail-archive-cancel">
                            Abbrechen
                        </Button>
                        <Button
                            onClick={() => setLifecycle("archived")}
                            disabled={lifecycleBusy}
                            data-testid="artikel-detail-archive-confirm"
                        >
                            {lifecycleBusy ? "..." : "Archivieren"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {article ? <AddArticleStockDialog key={article.id} open={addStockOpen} onClose={() => setAddStockOpen(false)} article={article} onSaved={() => {
                setAddStockOpen(false);
                setStockMessage("Bestand hinzugefügt.");
                load();
            }} /> : null}

            <ArtikelFormDialog
                open={editOpen}
                cloned={clonedId === id}
                onClose={() => setEditOpen(false)}
                categories={categories}
                article={article}
                onSaved={() => {
                    setEditOpen(false);
                    setClonedId(null);
                    setGalleryKey((k) => k + 1);
                    load();
                }}
            />

            {article ? (
                <QrPrintDialog
                    open={qrOpen}
                    onClose={() => setQrOpen(false)}
                    title="Artikel-QR-Codes drucken"
                    kind="article"
                    items={[{ id: article.id, title: article.name, subtitle: [article.ean, category?.name].filter(Boolean).join(" · ") }]}
                    testId="artikel-detail-qr-dialog"
                />
            ) : null}
        </>
    );
}
